/**
 * Pure helpers for `HmacTool`. Tests live in
 * `tests/web/hmacTool.test.ts`.
 *
 * Backed by `crypto.subtle.sign` — no third-party deps. Both modern
 * browsers and Node 18+ ship a Web Crypto implementation that handles
 * the HMAC-SHA-{1,256,384,512} family.
 */

export type HmacAlgo = 'SHA-1' | 'SHA-256' | 'SHA-384' | 'SHA-512';
export type IoFormat = 'utf-8' | 'hex' | 'base64';
export type OutputFormat = 'hex' | 'base64' | 'base64url';

export interface ComputeHmacInput {
  message: string;
  messageFormat: IoFormat;
  key: string;
  keyFormat: IoFormat;
  algo: HmacAlgo;
  output: OutputFormat;
}

/**
 * Compute HMAC-<algo>(key, message) and return it in `output` form.
 *
 * Throws with a Chinese message on bad encoding so the page can render
 * `e.message` directly as the error toast.
 */
export async function computeHmac(input: ComputeHmacInput): Promise<string> {
  const messageBytes = decodeInput(input.message, input.messageFormat, '消息');
  const keyBytes = decodeInput(input.key, input.keyFormat, '密钥');

  // `crypto.subtle` BufferSource types tightened in lib.dom.d.ts to
  // `Uint8Array<ArrayBuffer>`; our `Uint8Array` from `decodeInput` is
  // typed as `Uint8Array<ArrayBufferLike>` which the new types reject.
  // Casts here are runtime-safe — the buffer is always a real
  // ArrayBuffer (we never feed in SharedArrayBuffer-backed views).
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    requireNonEmptyKey(keyBytes) as BufferSource,
    { name: 'HMAC', hash: input.algo },
    /* extractable */ false,
    ['sign'],
  );

  const sigBuf = await crypto.subtle.sign('HMAC', cryptoKey, messageBytes as BufferSource);
  return encodeBytes(new Uint8Array(sigBuf), input.output);
}

function requireNonEmptyKey(buf: Uint8Array): Uint8Array {
  if (buf.byteLength === 0) {
    throw new Error('密钥为空');
  }
  return buf;
}

function decodeInput(value: string, format: IoFormat, label: '消息' | '密钥'): Uint8Array {
  // Empty messages are legal (HMAC of "" is well-defined and is even
  // a documented test vector); only the key has to be non-empty.
  switch (format) {
    case 'utf-8':
      return new TextEncoder().encode(value);
    case 'hex':
      return decodeHex(value, label);
    case 'base64':
      return decodeBase64Loose(value, label);
  }
}

function decodeHex(value: string, label: '消息' | '密钥'): Uint8Array {
  // Allow the user to paste hex with whitespace / colons (`aa bb cc`,
  // `aa:bb:cc`); reject anything else so a typo'd "0x" prefix doesn't
  // silently encode `0x` as bytes.
  const normalised = value.replace(/[\s:]/g, '');
  if (normalised.length === 0) return new Uint8Array(0);
  if (!/^[0-9a-fA-F]+$/.test(normalised)) {
    throw new Error(`${label}含非法 hex 字符`);
  }
  if (normalised.length % 2 !== 0) {
    throw new Error(`${label} hex 长度必须为偶数（实际：${normalised.length}）`);
  }
  const out = new Uint8Array(normalised.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(normalised.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

function decodeBase64Loose(value: string, label: '消息' | '密钥'): Uint8Array {
  const trimmed = value.replace(/\s+/g, '');
  if (trimmed.length === 0) return new Uint8Array(0);
  // Accept both standard and URL-safe alphabets, with or without
  // padding — users routinely copy from either source.
  const standard = trimmed.replace(/-/g, '+').replace(/_/g, '/');
  if (!/^[A-Za-z0-9+/]*=*$/.test(standard)) {
    throw new Error(`${label}不是合法 base64`);
  }
  const pad = (4 - (standard.length % 4)) % 4;
  const padded = pad === 0 ? standard : standard + '='.repeat(pad);
  let bin: string;
  try {
    bin = atob(padded);
  } catch {
    throw new Error(`${label}不是合法 base64`);
  }
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function encodeBytes(buf: Uint8Array, format: OutputFormat): string {
  switch (format) {
    case 'hex':
      return bytesToHex(buf);
    case 'base64':
      return bytesToBase64(buf, /* urlSafe */ false);
    case 'base64url':
      return bytesToBase64(buf, /* urlSafe */ true);
  }
}

function bytesToHex(buf: Uint8Array): string {
  let s = '';
  for (let i = 0; i < buf.length; i++) {
    s += buf[i].toString(16).padStart(2, '0');
  }
  return s;
}

function bytesToBase64(buf: Uint8Array, urlSafe: boolean): string {
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < buf.length; i += chunk) {
    bin += String.fromCharCode.apply(null, Array.from(buf.subarray(i, i + chunk)));
  }
  const std = btoa(bin);
  if (!urlSafe) return std;
  return std.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
